import { useState, useEffect } from 'react';
import api from '../api';
import {
  Eye,
  EyeOff,
  Edit2,
  RefreshCw,
  Save,
  X,
  RotateCcw,
  Gamepad2,
  Users,
  Clock,
  ArrowUpDown,
  MessageSquare,
  Hash,
} from 'lucide-react';

export default function Games() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/games');
      setGames(response.data.games || []);
    } catch (err) {
      console.error('Error fetching games:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      await api.post('/admin/games/sync');
      await fetchGames();
    } catch (err) {
      console.error('Error syncing games:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleVisibility = async (game) => {
    try {
      await api.put(`/admin/games/${game.gameId}`, { isHidden: !game.isHidden });
      fetchGames();
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  const handleEditStart = (game) => {
    setEditingGame(game.gameId);
    setEditForm({
      gameNameOverride: game.gameName !== game.originalName ? game.gameName : '',
      seatsOverride: game.override?.seatsOverride || '',
      orderingOverride: game.override?.orderingOverride ?? '',
      displayNotes: game.override?.displayNotes || '',
    });
  };

  const handleEditSave = async () => {
    try {
      await api.put(`/admin/games/${editingGame}`, {
        gameNameOverride: editForm.gameNameOverride || null,
        seatsOverride: editForm.seatsOverride ? parseInt(editForm.seatsOverride) : null,
        orderingOverride: editForm.orderingOverride !== '' ? parseInt(editForm.orderingOverride) : null,
        displayNotes: editForm.displayNotes || null,
      });
      setEditingGame(null);
      fetchGames();
    } catch (err) {
      console.error('Error saving game:', err);
    }
  };

  const handleResetOverride = async (game) => {
    if (!confirm(`Reset "${game.gameName}" to defaults?`)) return;
    try {
      await api.delete(`/admin/games/${game.gameId}/override`);
      fetchGames();
    } catch (err) {
      console.error('Error resetting override:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Gamepad2 className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Game Management</h1>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400 disabled:opacity-50 font-medium"
        >
          <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing...' : 'Sync from PokerAtlas'}
        </button>
      </div>

      {/* Games List */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Game</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tables</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Playing</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Waiting</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Visibility</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {games.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No games found. Click "Sync from PokerAtlas" to fetch games.
                  </td>
                </tr>
              ) : (
                games.map((game) => (
                  <tr key={game.id} className={`hover:bg-gray-700/30 ${game.isHidden ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium">{game.gameName}</p>
                        {game.hasOverride && game.gameName !== game.originalName && (
                          <p className="text-xs text-gray-500">Original: {game.originalName}</p>
                        )}
                        {game.override?.displayNotes && (
                          <p className="text-xs text-blue-400 mt-1">📝 {game.override.displayNotes}</p>
                        )}
                        {game.hasOverride && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded">
                            Modified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Gamepad2 size={16} />
                        {game.tables}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-green-400">
                        <Users size={16} />
                        {game.playing || 0}
                        {game.override?.seatsOverride && (
                          <span className="text-xs text-gray-500">/ {game.override.seatsOverride}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-blue-400">
                        <Clock size={16} />
                        {game.waiting || 0}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleVisibility(game)}
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                          game.isHidden
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}
                      >
                        {game.isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                        {game.isHidden ? 'Hidden' : 'Visible'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditStart(game)}
                          className="p-2 text-gray-400 hover:text-amber-500 hover:bg-gray-700 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        {game.hasOverride && (
                          <button
                            onClick={() => handleResetOverride(game)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                            title="Reset to Default"
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info */}
      <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-400">
        <p className="mb-2"><strong className="text-white">Tip:</strong> Changes are reflected immediately in the mobile app.</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Use <span className="text-amber-500">Hide</span> to temporarily remove games from the app display</li>
          <li>Use <span className="text-amber-500">Edit</span> to customize game names, seat counts, order, and notes</li>
          <li>Use <span className="text-amber-500">Reset</span> to restore original PokerAtlas data</li>
        </ul>
      </div>

      {/* Edit Modal */}
      {editingGame && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Edit Game</h2>
              <button onClick={() => setEditingGame(null)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Game Name Override */}
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                  <Gamepad2 size={14} /> Display Name
                </label>
                <input
                  type="text"
                  value={editForm.gameNameOverride}
                  onChange={(e) => setEditForm({ ...editForm, gameNameOverride: e.target.value })}
                  placeholder="Leave empty for original name"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>

              {/* Seats Override */}
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                  <Users size={14} /> Total Seats Override
                </label>
                <input
                  type="number"
                  value={editForm.seatsOverride}
                  onChange={(e) => setEditForm({ ...editForm, seatsOverride: e.target.value })}
                  placeholder="Leave empty for auto-calculated"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Override total seats (affects "available" calculation)</p>
              </div>

              {/* Ordering Override */}
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                  <ArrowUpDown size={14} /> Sort Order
                </label>
                <input
                  type="number"
                  value={editForm.orderingOverride}
                  onChange={(e) => setEditForm({ ...editForm, orderingOverride: e.target.value })}
                  placeholder="Lower numbers appear first"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Games are sorted by this number (1 = first)</p>
              </div>

              {/* Display Notes */}
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                  <MessageSquare size={14} /> Display Notes
                </label>
                <textarea
                  value={editForm.displayNotes}
                  onChange={(e) => setEditForm({ ...editForm, displayNotes: e.target.value })}
                  placeholder="Notes shown in app (e.g., 'New table!')"
                  rows={2}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white resize-none"
                />
              </div>

              {/* Save Button */}
              <button
                onClick={handleEditSave}
                className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 flex items-center justify-center gap-2"
              >
                <Save size={18} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
